import { useEffect, useMemo, useState } from "react";
import type { Patient, Session, Outcome } from "../types";
import { Icon } from "./Icon";
import { ageFromDob, fmtDateShort } from "../lib/format";

interface Props {
  patient: Patient;
  session: Session;
  outcomes: Outcome[];
  copiedKey: string | null;
  onCopy: (key: string, value: string, label: string) => void;
}

export function PatientHeader({ patient, session, outcomes, copiedKey, onCopy }: Props) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const startedMs = useMemo(() => new Date(session.startedAt).getTime(), [session.startedAt]);
  const elapsed = Math.max(0, Math.floor((now - startedMs) / 1000));
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  const age = useMemo(() => ageFromDob(patient.dob), [patient.dob]);
  const approved = outcomes.find((o) => o.status === "approved");

  return (
    <header className="phead">
      <div className="phead__left">
        <div className="phead__crumbs">
          <span>Patients</span>
          <Icon.Arrow width={11} height={11} />
          <span>Active session</span>
        </div>
        <h1 className="phead__name">
          <button
            className="phead__name-copy"
            onClick={() =>
              onCopy(
                "hdr:fullName",
                `${patient.firstName} ${patient.middleName} ${patient.lastName}`,
                "Full legal name",
              )
            }
          >
            <span>
              {patient.firstName} <span className="phead__name-mid">{patient.middleName}</span>{" "}
            </span>
            <span>{patient.lastName}</span>
            <span className="phead__copy-chip">
              {copiedKey === "hdr:fullName" ? (
                <Icon.Check width={12} height={12} />
              ) : (
                <Icon.Copy width={12} height={12} />
              )}
            </span>
          </button>
        </h1>
        <div className="phead__sub">
          <span>{age} years</span>
          <span className="phead__dot" />
          <button
            className="phead__chip"
            onClick={() => onCopy("hdr:dob", patient.dob, "Date of birth")}
          >
            <span>DOB {fmtDateShort(patient.dob)}</span>
            {copiedKey === "hdr:dob" ? (
              <Icon.Check width={11} height={11} />
            ) : (
              <Icon.Copy width={11} height={11} />
            )}
          </button>
          <span className="phead__dot" />
          <span>SSN ending in {patient.ssnLast4}</span>
          <span className="phead__dot" />
          <span>Patient ID {patient.id}</span>
        </div>
      </div>

      <div className="phead__right">
        <div className="phead__stat">
          <span className="phead__stat-label">Treatment</span>
          <span className="phead__stat-value">{session.treatmentType}</span>
        </div>
        <div className="phead__stat phead__stat--money">
          <span className="phead__stat-label">Requested</span>
          <button
            className="phead__stat-value phead__stat-value--copy"
            onClick={() =>
              onCopy("hdr:amt", String(session.requestedAmount), "Requested amount")
            }
          >
            <span className="phead__amt">${session.requestedAmount.toLocaleString()}</span>
            <span className="phead__copy-chip">
              {copiedKey === "hdr:amt" ? (
                <Icon.Check width={12} height={12} />
              ) : (
                <Icon.Copy width={12} height={12} />
              )}
            </span>
          </button>
        </div>
        <div className="phead__stat">
          <span className="phead__stat-label">Session</span>
          <span className="phead__stat-value phead__timer">{mm}:{ss}</span>
        </div>
        <div className="phead__stat">
          <span className="phead__stat-label">Status</span>
          {approved ? (
            <span className="phead__pill phead__pill--ok">
              <Icon.Check width={11} height={11} /> Approved · ${approved.amount?.toLocaleString()}
            </span>
          ) : (
            <span className="phead__pill phead__pill--neutral">In progress</span>
          )}
        </div>
      </div>
    </header>
  );
}
